import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  WHITE,
  PRIMARY_300,
  PRIMARY_600,
  GRAY_400,
  GRAY_200,
  GRAY_100,
  BLACK,
  PRIMARY_50
} from "../../constant/colors";
// Custom Figma icons
import { ReactComponent as AssigneeIcon } from "../../assets/svg/bulk-action-assignee.svg";
import { ReactComponent as FacilityIcon } from "../../assets/svg/bulk-action-facility.svg";
import { ReactComponent as PhysicianIcon } from "../../assets/svg/bulk-action-physician.svg";
import { ReactComponent as StatusIcon } from "../../assets/svg/bulk-action-status.svg";
import { ReactComponent as SelectAllIcon } from "../../assets/svg/bulk-action-select-all.svg";
import { ReactComponent as CloseIcon } from "../../assets/svg/bulk-action-close.svg";
import { getActionConfig, getActionKey, BULK_SELECTION_LIMIT } from "./bulkActionUtils";

// Delay before showing executing state (1 second)
const EXECUTING_STATE_DELAY_MS = 1000;

// Floating container positioned above pagination, centered horizontally
// Animates in from bottom when appearing, out to bottom when disappearing
// Uses fixed positioning to stay at bottom of viewport
// Centers within the main content area (accounting for sidebar width)
const BarContainer = styled.div`
  display: flex;
  position: fixed;
  bottom: 80px; /* Above pagination and horizontal scrollbar */
  left: calc(
    ${(props) => props.$sidebarWidth}px + (100vw - ${(props) => props.$sidebarWidth}px) / 2
  ); /* Sidebar width + half of remaining space */
  transform: translateX(-50%) translateY(${(props) => (props.$visible ? "0" : "20px")});
  background-color: ${WHITE};
  border-radius: 8px;
  box-shadow: 0px 24px 40px rgba(0, 0, 0, 0.2);
  padding: 8px;
  z-index: 100;
  align-items: center;
  gap: 16px;
  border-top: ${(props) => (props.$isExecuting ? `1px solid ${PRIMARY_300}` : "none")};
  cursor: ${(props) => (props.$isDelayPeriod || props.$isExecuting ? "not-allowed" : "default")};
  /* Allow popover dropdowns to extend outside the bar */
  overflow: visible;

  /* Slide animation for appear/disappear */
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  pointer-events: ${(props) => (props.$visible ? "auto" : "none")};
  transition: opacity 0.25s ease-out, transform 0.25s ease-out, visibility 0.25s ease-out;
`;

// Bordered container for selection tag - clickable to deselect all
const SelectionTag = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${WHITE};
  border: 1px solid ${GRAY_200};
  border-radius: 8px;
  padding: 8px;
  transition: background-color 0.2s ease;
  cursor: pointer;
  pointer-events: ${(props) => (props.$isDelayPeriod ? "none" : "inherit")};

  &:hover:not(:disabled) {
    background-color: ${GRAY_100};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

// Blue badge for count number inside the tag
const CountBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${PRIMARY_600};
  color: ${WHITE};
  border-radius: 8px;
  padding: 4px 6px;
  font-weight: 600;
  font-size: 14px;
  min-width: 20px;
  height: 20px;
  line-height: 1;
`;

// "selected" text next to the count
const SelectedText = styled.span`
  color: ${(props) => (props.$isExecuting ? GRAY_400 : BLACK)};
  font-weight: 600;
  font-size: 14px;
`;

// Select All button
const EscalationButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: ${BLACK};
    padding: 8px;
    height: auto;
    background: ${WHITE};
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    transition: color 0s;
    pointer-events: ${(props) => (props.$isDelayPeriod ? "none" : "inherit")};

    &:hover:not(:disabled) {
      color: ${BLACK};
      background: ${GRAY_100};
    }

    &:focus:not(:disabled) {
      color: ${BLACK};
      background: ${GRAY_100};
    }

    &:disabled {
      color: ${GRAY_400};
      background: ${WHITE};
      cursor: not-allowed;

      svg {
        path,
        circle,
        rect {
          stroke: ${GRAY_400};
          transition: stroke 0s;
        }
      }
    }
  }
`;

// Vertical divider between Select All and action buttons
const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  background-color: ${GRAY_200};
`;

// Wrapper for action buttons section - maintains width but hides content when executing
const ActionsWrapper = styled.div`
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity 0.25s ease-out, visibility 0.25s ease-out;

  > div {
    display: flex;
    align-items: center;
    gap: 16px;
    white-space: nowrap;
  }
`;

const ActionButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px;
    height: auto;
    border: none;
    color: ${(props) => (props.$isActive ? PRIMARY_600 : BLACK)};
    background: ${(props) => (props.$isActive ? PRIMARY_50 : WHITE)};
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    pointer-events: ${(props) => (props.$isDelayPeriod ? "none" : "inherit")};

    &:hover:not(:disabled) {
      color: ${(props) => (props.$isActive ? PRIMARY_600 : BLACK)};
      background: ${(props) => (props.$isActive ? PRIMARY_50 : GRAY_100)};
    }

    &:focus:not(:disabled) {
      color: ${(props) => (props.$isActive ? PRIMARY_600 : BLACK)};
      background: ${(props) => (props.$isActive ? PRIMARY_50 : GRAY_100)};
    }

    &:disabled {
      color: ${GRAY_400};
      background: ${WHITE};
      cursor: not-allowed;

      svg {
        opacity: 1;
        path,
        circle,
        rect {
          stroke: ${GRAY_400};
        }
      }
    }
  }
`;

// Wrapper to maintain width when switching between Select All button and Loading state
const SelectAllWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const LoadingContainer = styled.div`
  position: absolute;
  left: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${BLACK};
  padding-right: 8px;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity 0.25s ease-out, visibility 0.25s ease-out;
`;

const SelectAllButtonWrapper = styled.div`
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity 0.25s ease-out, visibility 0.25s ease-out;
`;

const LoadingText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${BLACK};
  white-space: nowrap;
`;

// Map base action types to icons
const ACTION_ICONS = {
  assignee_set: <AssigneeIcon />,
  assignees_set: <AssigneeIcon />,
  facility: <FacilityIcon />,
  physician: <PhysicianIcon />,
  status: <StatusIcon />
};

/**
 * Floating pill-shaped bar that appears when records are selected.
 * Positioned above pagination, centered horizontally.
 * Shows selection count badge, escalation link, divider, and action buttons.
 *
 * Implements delayed transition to executing state:
 * - When isExecuting becomes true, waits 1 second before showing executing UI
 * - During the delay, all buttons are disabled but normal UI remains visible
 * - If operation completes within 1 second, never shows executing state
 * - This prevents jarring transitions for quick operations
 */
const SIDEBAR_WIDTH_EXPANDED = 300;
const SIDEBAR_WIDTH_COLLAPSED = 80;

const BulkActionsBar = ({
  visible,
  selectionCount,
  totalRecords,
  availableActions,
  entityType,
  onClearSelection,
  onSelectAll,
  onActionClick,
  isExecuting,
  renderActionDropdown,
  activeAction,
  isSidebarCollapsed
}) => {
  const { t } = useTranslation();

  // Ref to the bar container for popover positioning
  const barContainerRef = useRef(null);

  // Delayed executing state - only show executing UI after 1 second delay
  const [showExecutingState, setShowExecutingState] = useState(false);
  const executingTimeoutRef = useRef(null);

  // Handle delayed transition to executing state
  useEffect(() => {
    if (isExecuting) {
      // Start timer to show executing state after delay
      executingTimeoutRef.current = setTimeout(() => {
        setShowExecutingState(true);
      }, EXECUTING_STATE_DELAY_MS);
    } else {
      // Clear timer if operation completes before delay
      if (executingTimeoutRef.current) {
        clearTimeout(executingTimeoutRef.current);
        executingTimeoutRef.current = null;
      }
      // Reset executing state
      setShowExecutingState(false);
    }

    // Cleanup on unmount
    return () => {
      if (executingTimeoutRef.current) {
        clearTimeout(executingTimeoutRef.current);
      }
    };
  }, [isExecuting]);

  const maxSelectableCount = Math.min(totalRecords, BULK_SELECTION_LIMIT);
  const canEscalate = selectionCount < maxSelectableCount;
  const showSelectAll = totalRecords <= BULK_SELECTION_LIMIT;

  const getEscalationText = () => {
    if (showSelectAll) {
      return t("bulk_actions.select_all");
    }
    return t("bulk_actions.select_first", { count: BULK_SELECTION_LIMIT });
  };

  // Filter out assignee mode variants — only show assignees_set as the representative button
  const displayActions = availableActions.filter(
    (action) => action.actionType !== "assignees_add" && action.actionType !== "assignees_remove"
  );

  // Determine if we're in the delay period (executing but not showing executing state yet)
  const isDelayPeriod = isExecuting && !showExecutingState;

  const activeActionKey = activeAction ? getActionKey(activeAction) : null;

  return (
    <BarContainer
      ref={barContainerRef}
      $visible={visible}
      $isExecuting={showExecutingState}
      $isDelayPeriod={isDelayPeriod}
      $sidebarWidth={isSidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED}
      data-testid="bulk-action-bar"
    >
      {/* Selection tag with count badge, "selected" text, and close button - entire tag is clickable */}
      <SelectionTag
        onClick={onClearSelection}
        disabled={showExecutingState}
        $isDelayPeriod={isDelayPeriod}
        data-testid="selection-count"
        data-pendo="bulk_action.clear_selection"
      >
        <CountBadge>{selectionCount}</CountBadge>{" "}
        <SelectedText $isExecuting={showExecutingState}>{t("bulk_actions.selected_label")}</SelectedText>
        <CloseIcon />
      </SelectionTag>

      {/* Select All button and Loading state - both kept in DOM to maintain width */}
      <SelectAllWrapper>
        <LoadingContainer $visible={showExecutingState} data-testid="bulk-action-loading">
          <Spin indicator={<LoadingOutlined spin />} size="small" />
          <LoadingText>{t("bulk_actions.loading.inline_message")}</LoadingText>
        </LoadingContainer>
        <SelectAllButtonWrapper $visible={!showExecutingState}>
          <EscalationButton
            type="text"
            onClick={onSelectAll}
            disabled={!canEscalate || showExecutingState}
            $isDelayPeriod={isDelayPeriod}
            data-testid="select-all-button"
            data-pendo={showSelectAll ? "bulk_action.select_all" : "bulk_action.select_first_1000"}
          >
            <SelectAllIcon />
            {getEscalationText()}
          </EscalationButton>
        </SelectAllButtonWrapper>
      </SelectAllWrapper>

      {/* Action buttons section - animates width when executing (after delay) */}
      <ActionsWrapper $visible={!showExecutingState}>
        <div>
          <Divider />

          {/* Action buttons */}
          {displayActions.map((action) => {
            const actionKey = getActionKey(action);
            const actionConfig = getActionConfig(action, entityType);
            const icon = ACTION_ICONS[action.actionType];

            const basePendo = action.actionType.replace("_set", "").replace("assignees", "assignee");
            const pendoKey = action.targetEntity ? `${action.targetEntity}_${basePendo}` : basePendo;

            const button = (
              <ActionButton
                type="text"
                onClick={() => onActionClick(action)}
                disabled={showExecutingState}
                $isDelayPeriod={isDelayPeriod}
                $isActive={activeActionKey === actionKey}
                data-testid={`bulk-action-${actionKey}`}
                data-pendo={`bulk_action.attribute.${pendoKey}`}
              >
                {icon}
                {t(actionConfig.labelKey)}
              </ActionButton>
            );

            // Wrap with dropdown if renderActionDropdown is provided
            // Pass barContainerRef so dropdown can attach to the fixed bulk action bar
            if (renderActionDropdown) {
              return (
                <React.Fragment key={actionKey}>{renderActionDropdown(action, button, barContainerRef)}</React.Fragment>
              );
            }

            return <React.Fragment key={actionKey}>{button}</React.Fragment>;
          })}
        </div>
      </ActionsWrapper>
    </BarContainer>
  );
};

BulkActionsBar.propTypes = {
  visible: PropTypes.bool,
  selectionCount: PropTypes.number.isRequired,
  totalRecords: PropTypes.number.isRequired,
  availableActions: PropTypes.arrayOf(
    PropTypes.shape({
      actionType: PropTypes.string.isRequired,
      targetEntity: PropTypes.string
    })
  ).isRequired,
  entityType: PropTypes.string,
  onClearSelection: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onActionClick: PropTypes.func.isRequired,
  isExecuting: PropTypes.bool,
  renderActionDropdown: PropTypes.func,
  activeAction: PropTypes.shape({
    actionType: PropTypes.string.isRequired,
    targetEntity: PropTypes.string
  }),
  isSidebarCollapsed: PropTypes.bool
};

BulkActionsBar.defaultProps = {
  visible: false,
  isExecuting: false,
  entityType: null,
  renderActionDropdown: null,
  activeAction: null,
  isSidebarCollapsed: false
};

export default BulkActionsBar;
